import React, { useState } from 'react';
import { Star } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

function StarRating({ rating = 5 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${n <= rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`}
        />
      ))}
    </div>
  );
}

function Avatar({ name, avatar_url }) {
  if (avatar_url) {
    return (
      <img
        src={avatar_url}
        alt={name}
        className="w-10 h-10 rounded-full object-cover"
      />
    );
  }
  const initials = (name || 'A')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-xs font-semibold text-stone-600">
      {initials}
    </div>
  );
}

/**
 * Testimonials Block Renderer
 * Variants: cards, carousel, quotes, minimal
 */
export default function TestimonialsRenderer({ props, variant }) {
  const {
    headline = 'What Our Customers Say',
    subheadline = '',
    items = [],
    show_ratings = true,
    background_color = '#FAFAF5',
  } = props;

  const [carouselIndex, setCarouselIndex] = useState(0);

  const headerEl = (
    <div className="text-center mb-12">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
      {subheadline && (
        <p
          className="text-lg text-stone-500 max-w-2xl mx-auto"
          style={{ fontFamily: BODY_FONT }}
        >
          {subheadline}
        </p>
      )}
    </div>
  );

  if (!items.length) {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <p className="text-center text-stone-400 text-sm">No testimonials added yet.</p>
        </div>
      </section>
    );
  }

  // Cards variant (grid)
  if (variant === 'cards') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <div className={`grid grid-cols-1 ${items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
            {items.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 flex flex-col"
              >
                {show_ratings && item.rating && (
                  <div className="mb-3">
                    <StarRating rating={item.rating} />
                  </div>
                )}
                <p
                  className="text-stone-600 text-sm leading-relaxed flex-1 mb-4"
                  style={{ fontFamily: BODY_FONT }}
                >
                  "{item.quote}"
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-stone-100">
                  <Avatar name={item.name} avatar_url={item.avatar_url} />
                  <div>
                    <p
                      className="text-sm font-semibold text-stone-900"
                      style={{ fontFamily: HEADING_FONT }}
                    >
                      {item.name}
                    </p>
                    {(item.role || item.company) && (
                      <p className="text-xs text-stone-400" style={{ fontFamily: BODY_FONT }}>
                        {[item.role, item.company].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Carousel variant
  if (variant === 'carousel') {
    const current = items[carouselIndex] || items[0];
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-3xl mx-auto">
          {headerEl}
          <div className="text-center">
            {show_ratings && current.rating && (
              <div className="flex justify-center mb-4">
                <StarRating rating={current.rating} />
              </div>
            )}
            <p
              className="text-xl sm:text-2xl text-stone-700 leading-relaxed mb-6"
              style={{ fontFamily: BODY_FONT }}
            >
              "{current.quote}"
            </p>
            <div className="flex items-center justify-center gap-3 mb-8">
              <Avatar name={current.name} avatar_url={current.avatar_url} />
              <div className="text-left">
                <p
                  className="text-sm font-semibold text-stone-900"
                  style={{ fontFamily: HEADING_FONT }}
                >
                  {current.name}
                </p>
                {(current.role || current.company) && (
                  <p className="text-xs text-stone-400" style={{ fontFamily: BODY_FONT }}>
                    {[current.role, current.company].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            </div>
            {items.length > 1 && (
              <div className="flex justify-center gap-2">
                {items.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCarouselIndex(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      i === carouselIndex ? 'bg-stone-800' : 'bg-stone-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Quotes variant (large stacked quotes)
  if (variant === 'quotes') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-4xl mx-auto">
          {headerEl}
          <div className="space-y-12">
            {items.map((item, i) => (
              <div key={i} className="relative pl-12">
                <span
                  className="absolute left-0 top-0 text-6xl leading-none text-stone-200 select-none"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  &ldquo;
                </span>
                <div>
                  {show_ratings && item.rating && (
                    <div className="mb-2">
                      <StarRating rating={item.rating} />
                    </div>
                  )}
                  <p
                    className="text-lg sm:text-xl text-stone-700 leading-relaxed mb-4"
                    style={{ fontFamily: BODY_FONT }}
                  >
                    {item.quote}
                  </p>
                  <div className="flex items-center gap-3">
                    <Avatar name={item.name} avatar_url={item.avatar_url} />
                    <div>
                      <p
                        className="text-sm font-semibold text-stone-900"
                        style={{ fontFamily: HEADING_FONT }}
                      >
                        {item.name}
                      </p>
                      {(item.role || item.company) && (
                        <p className="text-xs text-stone-400" style={{ fontFamily: BODY_FONT }}>
                          {[item.role, item.company].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Minimal variant
  if (variant === 'minimal') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-3xl mx-auto">
          {headerEl}
          <div className="space-y-6">
            {items.map((item, i) => (
              <div key={i} className="py-4 border-b border-stone-200 last:border-0">
                {show_ratings && item.rating && (
                  <div className="mb-2">
                    <StarRating rating={item.rating} />
                  </div>
                )}
                <p
                  className="text-stone-600 text-sm leading-relaxed mb-2"
                  style={{ fontFamily: BODY_FONT }}
                >
                  "{item.quote}"
                </p>
                <p className="text-xs text-stone-400" style={{ fontFamily: BODY_FONT }}>
                  -- {item.name}
                  {(item.role || item.company) &&
                    `, ${[item.role, item.company].filter(Boolean).join(', ')}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Fallback to cards
  return (
    <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
      <div className="max-w-6xl mx-auto">
        {headerEl}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
              {show_ratings && item.rating && (
                <div className="mb-3"><StarRating rating={item.rating} /></div>
              )}
              <p className="text-stone-600 text-sm leading-relaxed mb-4" style={{ fontFamily: BODY_FONT }}>
                "{item.quote}"
              </p>
              <div className="flex items-center gap-3 pt-3 border-t border-stone-100">
                <Avatar name={item.name} avatar_url={item.avatar_url} />
                <div>
                  <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: HEADING_FONT }}>{item.name}</p>
                  {(item.role || item.company) && (
                    <p className="text-xs text-stone-400">{[item.role, item.company].filter(Boolean).join(', ')}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
